import type { Schemas } from '@npc/api-client'
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { api, API_BASE } from './api'
import { activityTimeBounds, type ActivityQuery } from './activity-query'
import { call } from './errors'
import {
  cancelDevelopmentTask,
  claimDevelopmentTask,
  createDevelopmentTask,
  getDevelopmentTask,
  getDevelopmentTaskEvents,
  getDevelopmentTaskOptions,
  listProjectDevelopmentTasks,
  type DevelopmentTaskListQuery,
  prepareDevelopmentTask,
  requestDevelopmentTaskPreview,
  type CreateDevelopmentTaskInput,
  type DevelopmentTaskExecutorIdentity,
  type DevelopmentTaskPrepareInput,
  addDevelopmentTaskComment,
  listDevelopmentTaskComments,
} from './development-tasks'
import { developmentTaskPreviewReceiptKey, rememberPreviewReceipt } from './preview-receipt-key'
import { getDebugSkills } from './debug-skills'
import {
  createImprovementProposal,
  getAiModule,
  getImprovementProposal,
  getModuleEvaluationRun,
  getVerifiedLearningEntry,
  listAiModules,
  listImprovementProposals,
  listModuleEvaluationRuns,
  listVerifiedLearningEntries,
  retryImprovementProposal,
  disableVerifiedLearningEntry,
  type AiOsModuleFilter,
  type ImprovementProposalCreate,
  type LearningEntryDisableRequest,
  type ProposalRetryRequest,
} from './ai-os'
import {
  createProjectImport,
  getProjectImport,
  getProjectImportByProject,
  approveProjectImport,
  unlinkProjectImport,
  reissueProjectImportPackage,
  type ProjectImportCreate,
  type ProjectImportUnlink,
  type ProjectImport,
} from './project-imports'
import {
  linkProjectRepository,
  listProjectRepositories,
  listProjectRepositoryCandidates,
  type ProjectRepositories,
  type ProjectRepositoryLinkRequest,
} from './project-repositories'
import { noMoreEvents } from './onboarding-status'
import {
  approveStaticSiteAction,
  createStaticSite,
  createStaticSitePreview,
  fastPublishStaticSiteFor,
  fastRollbackStaticSiteFor,
  getProjectStaticSiteSummary,
  listStaticSitePreviews,
  revokeStaticSitePreview,
  listStaticSiteDeploymentsFor,
  listStaticSiteVersionsFor,
  listStaticSitesFor,
  newIdempotencyKey,
  publishStaticSite,
  requestStaticSiteApproval,
  staticSiteDeploymentsRefetchMs,
  uploadStaticSiteVersion,
  type StaticPublishScope,
  type StaticSiteApprovalGrant,
} from './static-sites'
import { render } from '../copy'
import {
  getProjectFastDelivery,
  listProjectFastDeliveries,
  projectFastDeliveriesQueryKey,
  projectFastDeliveryQueryKey,
} from './fast-deliveries'

/**
 * 服务端状态。屏里不出现 `fetch`，也不出现路径字符串——都收在这儿。
 *
 * 每个 hook 的返回类型都是生成物里的类型，没有一处手写的接口形状。
 * 契约一改，`make generate-api` 之后这个文件就是第一个报错的地方，这正是想要的。
 */

/**
 * 每一次调用都过 `call()`（见 `app/errors.ts`）。它把三种结局分开：
 * **连不上后端 / 后端说你没登录 / 后端说了别的**——这三种的修法完全不同，
 * 混成一句「出错了」会让人去查登录，而毛病在反代上。
 *
 * 第一个参数是路径，只用来写诊断日志，不上屏。
 */

export const queryKeys = {
  me: ['auth', 'me'] as const,
  loginDevices: ['auth', 'sessions'] as const,
  backups: ['ops', 'backups'] as const,
  account: ['account'] as const,
  hosts: ['hosts'] as const,
  securityReport: ['security', 'report'] as const,
  unassignedHosts: ['hosts', 'unassigned'] as const,
  host: (hostId: string) => ['hosts', hostId] as const,
  hostMetrics: (hostId: string) => ['hosts', hostId, 'metrics'] as const,
  hostActions: (hostId: string) => ['hosts', hostId, 'actions'] as const,
  hostSplitPlan: (hostId: string) => ['hosts', hostId, 'split-plan'] as const,
  projects: ['projects'] as const,
  projectsSummary: ['projects', 'summary'] as const,
  aiSteward: ['ai-steward-settings'] as const,
  aiStewardProject: (projectId: string) => ['ai-steward-settings', projectId] as const,
  aiDeveloper: ['ai-developer-settings'] as const,
  aiPatchGeneratorRoot: ['ai-patch-generator-settings'] as const,
  aiPatchGenerator: (projectId?: string) =>
    ['ai-patch-generator-settings', projectId ?? 'global'] as const,
  aiReviewerRoot: ['ai-reviewer-settings'] as const,
  aiReviewer: (projectId?: string) => ['ai-reviewer-settings', projectId ?? 'global'] as const,
  project: (projectId: string) => ['projects', projectId] as const,
  projectHosts: (projectId: string) => ['projects', projectId, 'hosts'] as const,
  projectResources: (projectId: string) => ['projects', projectId, 'resources'] as const,
  projectDevelopmentTasks: (projectId: string, filter: DevelopmentTaskListQuery = {}) =>
    ['projects', projectId, 'development-tasks', filter] as const,
  projectImport: (importId: string) => ['project-imports', importId] as const,
  projectImportByProject: (projectId: string) => ['projects', projectId, 'import'] as const,
  developmentTask: (taskId: string) => ['development-tasks', taskId] as const,
  developmentTaskEvents: (taskId: string) => ['development-tasks', taskId, 'events'] as const,
  developmentTaskPreviewReceipt: developmentTaskPreviewReceiptKey,
  developmentTaskOptions: (projectId: string) =>
    ['projects', projectId, 'development-task-options'] as const,
  projectRepositories: (projectId: string) => ['projects', projectId, 'repositories'] as const,
  projectRepositoryCandidates: (projectId: string) =>
    ['projects', projectId, 'repository-candidates'] as const,
  projectFastDeliveries: projectFastDeliveriesQueryKey,
  projectFastDelivery: projectFastDeliveryQueryKey,
  staticSites: (projectId: string) => ['projects', projectId, 'static-sites'] as const,
  staticSiteSummary: (projectId: string) =>
    ['projects', projectId, 'static-sites', 'summary'] as const,
  staticSiteVersions: (projectId: string, siteId: string) =>
    ['projects', projectId, 'static-sites', siteId, 'versions'] as const,
  staticSiteDeployments: (projectId: string, siteId: string) =>
    ['projects', projectId, 'static-sites', siteId, 'deployments'] as const,
  staticSitePreviews: (projectId: string, siteId: string) =>
    ['projects', projectId, 'static-sites', siteId, 'previews'] as const,
  accountStaticSites: ['static-sites'] as const,
  accountStaticSiteVersions: (siteId: string) => ['static-sites', siteId, 'versions'] as const,
  accountStaticSiteDeployments: (siteId: string) =>
    ['static-sites', siteId, 'deployments'] as const,
  vendors: ['vendors'] as const,
  today: ['today'] as const,
  dashboard: ['dashboard'] as const,
  activity: (opts: ActivityQuery) =>
    [
      'activity',
      opts.view,
      opts.projectId ?? null,
      opts.scope ?? null,
      opts.type ?? null,
      opts.status ?? null,
      opts.actor ?? null,
      opts.q ?? null,
      opts.time ?? null,
      opts.before ?? null,
      opts.limit ?? null,
    ] as const,
  activityItem: (id: string) => ['activity', 'item', id] as const,
  timeline: (opts: TimelineQuery) =>
    [
      'timeline',
      opts.filter,
      opts.projectId ?? null,
      opts.hostId ?? null,
      opts.limit ?? null,
    ] as const,
  action: (actionId: string) => ['actions', actionId] as const,
  debugSkills: ['debug-skills'] as const,

  /**
   * 模块演进那一组。**筛选条件进 key**：不进的话，「全部」与「只看某个模块」
   * 会共用同一份缓存，切一下筛选看到的是上一次那批，而且看不出为什么。
   */
  aiModules: (projectId: string) => ['projects', projectId, 'ai-os', 'modules'] as const,
  aiModule: (projectId: string, moduleId: string) =>
    ['projects', projectId, 'ai-os', 'modules', moduleId] as const,
  aiEvaluations: (projectId: string, moduleId?: string) =>
    ['projects', projectId, 'ai-os', 'evaluations', moduleId ?? null] as const,
  aiEvaluation: (projectId: string, evaluationId: string) =>
    ['projects', projectId, 'ai-os', 'evaluations', 'one', evaluationId] as const,
  aiProposals: (projectId: string, moduleId?: string) =>
    ['projects', projectId, 'ai-os', 'proposals', moduleId ?? null] as const,
  aiProposal: (projectId: string, proposalId: string) =>
    ['projects', projectId, 'ai-os', 'proposals', 'one', proposalId] as const,
  aiLearning: (projectId: string, moduleId?: string) =>
    ['projects', projectId, 'ai-os', 'learning', moduleId ?? null] as const,
  aiLearningEntry: (projectId: string, entryId: string) =>
    ['projects', projectId, 'ai-os', 'learning', 'one', entryId] as const,
}

export function isDevelopmentTaskFinal(state: string | undefined): boolean {
  return state === 'ready' || state === 'cancelled' || state === 'failed'
}

// ------------------------------------------------------------------ 登录

/**
 * 发验证码。
 *
 * 普通注册先走这条证明邮箱归属，再在已登录状态设置密码；它也保留为换设备时的兜底。
 */
export function useRequestEmailCode() {
  return useMutation({
    mutationFn: async (email: string) =>
      call('/auth/email-code', () => api.POST('/auth/email-code', { body: { email } })),
  })
}
export function usePasswordLogin() {
  return useMutation({
    mutationFn: async (input: Schemas['PasswordCredentials']) =>
      call('/auth/login', () => api.POST('/auth/login', { body: input })),
  })
}

/**
 * 退出登录。POST /auth/logout 服务端早就有；这是界面侧的出口——
 * 「能退出」是登录这件事的另一半，没有它用户只剩关浏览器。
 */
export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => call('/auth/logout', () => api.POST('/auth/logout')),
    onSuccess: () => {
      queryClient.clear()
      window.location.assign('/auth')
    },
  })
}

/**
 * 二次验证·密码确认：当前密码换一次性 step-up 令牌（X-Npc-Step-Up 头用）。
 * Passkey 退出后的第一把钥匙；令牌消费端与服务端五道关不变。
 */
export function useStepUpWithPassword() {
  return useMutation({
    mutationFn: async (input: { password: string; intent: Schemas['StepUpIntent']; resource?: string }) =>
      call('/auth/step-up/password', () =>
        api.POST('/auth/step-up/password', {
          body: { password: input.password, intent: input.intent, resource: input.resource ?? '' } as never,
        }),
      ),
  })
}

/** 二次验证·邮箱确认码：给本账号邮箱发一枚确认码。 */
export function useRequestStepUpCode() {
  return useMutation({
    mutationFn: async (input: { intent: Schemas['StepUpIntent']; resource?: string }) =>
      call('/auth/step-up/email-code', () =>
        api.POST('/auth/step-up/email-code', {
          body: { intent: input.intent, resource: input.resource ?? '' },
        }),
      ),
  })
}

/** 二次验证·邮箱确认码验码换令牌。 */
export function useVerifyStepUpCode() {
  return useMutation({
    mutationFn: async (input: { code: string; intent: Schemas['StepUpIntent']; resource?: string }) =>
      call('/auth/step-up/email-code/verify', () =>
        api.POST('/auth/step-up/email-code/verify', {
          body: { code: input.code, intent: input.intent, resource: input.resource ?? '' },
        }),
      ),
  })
}

/**
 * 现在有哪几台设备登录着。
 *
 * 设备名由服务端渲染（`auth.DeviceName`）。**前端不要自己解析 User-Agent**：
 * 两份解析迟早分家，而分家的表现是同一台设备在设置页与审计里叫两个名字，
 * 而那正是他要拿来判断「这台是不是我」的东西。
 */
export function useLoginDevices() {
  return useQuery({
    queryKey: queryKeys.loginDevices,
    queryFn: async () => call('/auth/sessions', () => api.GET('/auth/sessions')),
  })
}

/**
 * 把某一台退掉。**要一张绑到这条 sessionId 上的二次验证令牌。**
 *
 * 令牌由调用方（`AccountScreen` 的确认框）先换好传进来：
 * 换令牌那一段是通用的，四处红级动作共用 `components/step-up.tsx` 一份。
 */
export function useRevokeLoginDevice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { sessionId: string; stepUpToken: string }) =>
      call('/auth/sessions/{sessionId}', () =>
        api.DELETE('/auth/sessions/{sessionId}', {
          params: {
            path: { sessionId: input.sessionId },
            header: { 'X-Npc-Step-Up': input.stepUpToken },
          },
        }),
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.loginDevices })
    },
  })
}

/**
 * 除了这台，其他全退掉。
 *
 * **刻意不要二次验证**：这是「我怀疑号被人登了」时唯一有用的动作，
 * 而收紧安全的动作不该被安全措施挡住（服务端
 * `SignOutEverywhereExcept` 的注释写的是同一句）。
 */
export function useRevokeOtherLoginDevices() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      call('/auth/sessions/others', () => api.DELETE('/auth/sessions/others')),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.loginDevices })
    },
  })
}

/**
 * 平台自己那个数据库备份得怎么样。
 *
 * **只读，不加门槛。** 一次聚合，不拼四个请求——四个请求会在四个时刻
 * 各自成功或失败，于是屏上可能同时出现「最近成功：今天」与「从来没备份过」。
 */
export function useBackupOverview() {
  return useQuery({
    queryKey: queryKeys.backups,
    queryFn: async () => call('/ops/backups', () => api.GET('/ops/backups')),
    // 有活在跑时它会自己往前走，隔一会儿看一眼。
    refetchInterval: 30_000,
  })
}

/**
 * 现在备份一份。
 *
 * **刻意不要二次验证**：拍一份新快照不动任何现有数据，也不删任何东西。
 * 在它前面加一道门，等于让「多留一份底」变难，而这个产品的原则是
 * 可回退比可控制重要。资格由 `backup.manage` 管（只有老板）。
 *
 * 它只会留下一条**排队中**的记录——真正去跑的是 srv01 上那个执行器。
 * 执行器没接上时这条会一直排着，界面照实说，**不会显示成成功**。
 */
export function useRequestBackup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      call('/ops/backups/runs', () =>
        api.POST('/ops/backups/runs', {
          params: { header: { 'X-Idempotency-Key': crypto.randomUUID() } },
        }),
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backups })
    },
  })
}

/**
 * 拿这份备份演练一次恢复。
 *
 * 要一张绑到**这一份**上的二次验证令牌（intent `restore_drill`）：
 * 演练会在跑生产库那台机器上建库、恢复、删库——全程隔离，
 * 但它是唯一一个会让另一台机器上发生写操作的入口，而「会写」就是这道门的判据。
 */
export function useRequestRestoreDrill() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { runId: string; stepUpToken: string }) =>
      call('/ops/backups/{runId}/restore-drills', () =>
        api.POST('/ops/backups/{runId}/restore-drills', {
          params: {
            path: { runId: input.runId },
            header: {
              'X-Npc-Step-Up': input.stepUpToken,
              'X-Idempotency-Key': crypto.randomUUID(),
            },
          },
        }),
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backups })
    },
  })
}

export function useUpdatePassword() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: Schemas['PasswordUpdateRequest']) =>
      call('/auth/password', () => api.PUT('/auth/password', { body: input })),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.me })
    },
  })
}
/** 用验证码换登录态。会话是 httpOnly Cookie，前端拿不到也不该拿。 */
export function useVerifyEmailCode() {
  return useMutation({
    mutationFn: async (input: { email: string; code: string }) =>
      call('/auth/email-code/verify', () => api.POST('/auth/email-code/verify', { body: input })),
  })
}

/**
 * 我是谁。**`has_passkey` 是这一条最要紧的字段。**
 *
 * arron 现在每登录一次消耗一张恢复码（还剩 9 张），而邮箱验证码那条路不通——
 * 也就是说他唯一的路是消耗性的，且正在耗尽。而「他还没注册指纹」这件事
 * API 里一直有（`User.has_passkey`），**界面上没有出口**。
 */
export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => call('/auth/me', () => api.GET('/auth/me')),
  })
}

export function useAccountMembership(enabled = true) {
  return useQuery({
    queryKey: queryKeys.account,
    queryFn: async () => call('/account', () => api.GET('/account')),
    enabled,
  })
}
// ------------------------------------------------------------------ 机器

export function useHosts() {
  return useQuery({
    queryKey: queryKeys.hosts,
    queryFn: async () => call('/hosts', () => api.GET('/hosts')),
  })
}

/**
 * 全平台安全体检。
 *
 * **聚合在后端。** 前端拿 20 台机器各查一遍再自己算「哪条影响几台」，
 * 等后端真做了就是两份判据——而两份判据分家时，界面与接口会各说各话。
 * `level`（得管 / 找空补）同理由后端判，这里只负责显示。
 */
export function useSecurityReport() {
  return useQuery({
    queryKey: queryKeys.securityReport,
    queryFn: async () => call('/security/report', () => api.GET('/security/report')),
  })
}

export function useUnassignedHosts() {
  return useQuery({
    queryKey: queryKeys.unassignedHosts,
    queryFn: async () =>
      call('/hosts?unassigned=true', () =>
        api.GET('/hosts', { params: { query: { unassigned: true } } }),
      ),
  })
}

export function useHost(hostId: string) {
  return useQuery({
    queryKey: queryKeys.host(hostId),
    queryFn: async () =>
      call('/hosts/{hostId}', () => api.GET('/hosts/{hostId}', { params: { path: { hostId } } })),
  })
}

export function useHostMetrics(hostId: string) {
  return useQuery({
    queryKey: queryKeys.hostMetrics(hostId),
    queryFn: async () =>
      call('/hosts/{hostId}/metrics', () =>
        api.GET('/hosts/{hostId}/metrics', {
          params: { path: { hostId }, query: { window: '24h', metrics: 'cpu,memory,disk' } },
        }),
      ),
  })
}

/**
 * 「我最近在这台上做了什么」。
 *
 * **这一块以前是空的，而后端一直挂着。**
 *
 * 这儿原来有一段注释写着「契约里没有这个接口」，数据从 mock 出、
 * 生产构建里返回空数组——也就是**线上这一块永远是空的**。
 * 那句话在写下的那一刻是对的，而 `GET /hosts/{hostId}/actions` 后来落地了，
 * **没有任何东西会在那一刻提醒人回来**。CLAUDE.md 里管这个叫
 * 「契约落地那天，回头核一遍所有说『还没有』的话」——这就是一例，
 * 而且它的表现最阴：不是报错，是一块永远空着的区域。
 *
 * 形状当初就是照 `TimelineEntry` 定死的，所以接上来只换了这几行，屏一个字没动。
 */
export function useHostActions(hostId: string) {
  return useQuery({
    queryKey: queryKeys.hostActions(hostId),
    queryFn: async () =>
      call('/hosts/{hostId}/actions', () =>
        api.GET('/hosts/{hostId}/actions', { params: { path: { hostId } } }),
      ),
  })
}

/** 生成机器拆分方案预览。只给方案，不执行。 */
export function usePreviewHostSplitPlan(hostId: string) {
  return useMutation({
    mutationFn: async (input: Schemas['HostSplitPlanPreviewRequest']) =>
      call('/hosts/{hostId}/split-plan/preview', () =>
        api.POST('/hosts/{hostId}/split-plan/preview', {
          params: { path: { hostId } },
          body: input,
        }),
      ),
  })
}

/**
 * 「我还进得去吗」。
 *
 * 这一条回答的是 arron 那条底线：**万一平台挂了，他还能不能自己进服务器。**
 * 接入时平台往他的 `authorized_keys` 里写了一行，而 2026-08-08 之前那一步
 * 会把他自己的公钥一起删掉——这个接口就是那件事的用户可见出口。
 *
 * **刻意不带 `?live=1`**：契约把「现在去连一次」做成了可选的，
 * 而默认那条**连不上也不报错**。这一页的价值恰恰在平台不正常的时候，
 * 那时它更该打得开——只是会多出「这是存档」那一行（`live: false`）。
 */
export function useHostAccessMethod(hostId: string) {
  return useQuery({
    queryKey: ['hosts', hostId, 'access-method'] as const,
    queryFn: async () =>
      call('/hosts/{hostId}/access-method', () =>
        api.GET('/hosts/{hostId}/access-method', { params: { path: { hostId } } }),
      ),
  })
}

/**
 * 「现在重新看一眼」。
 *
 * `.neither` 那一档的唯一出口：两台机器都报不出名字时，
 * 「对／不对」两个按钮都渲染不出来（它们都要一个名字），
 * 而**让他确认一次「两台他都叫不出名字的机器之间的搬迁」，
 * 等于让他点一个他没法判断的按钮**。
 *
 * 这个动作他能判断（不需要认出任何一台），也真能解决问题
 * （重新探测多半能拿到至少一个名字，那时就退回前三档，按钮就有名字了）。
 */
export function useRecheckHost(hostId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () =>
      call('/hosts/{hostId}/recheck', () =>
        api.POST('/hosts/{hostId}/recheck', { params: { path: { hostId } } }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hosts })
    },
  })
}

/** 给 AI 一把临时钥匙。默认 2 小时，契约里的 `ttl_seconds` 默认值就是 7200。 */
export function useIssueHostAccess(hostId: string) {
  return useMutation({
    mutationFn: async (reason: string) =>
      call('/hosts/{hostId}/access', () =>
        api.POST('/hosts/{hostId}/access', {
          params: { path: { hostId } },
          body: { reason },
        }),
      ),
  })
}

/**
 * 认下一条建议里的**一件**事。
 *
 * ## `what` 是必传的，尽管契约允许不传
 *
 * 契约里不传 `what` 等于「认全部」，而**这个前端一次都不许那么调**。
 *
 * 一条建议里可能同时有两件事：「我猜这台叫小食堂」（`identity`）和
 * 「它是不是搬到另一台机器上面去了」（`parent`）。
 * **这两件事要求用户做的下一个动作不一样**——
 *
 * > 合成一个按钮的话，他为了确认名字点一下，
 * > **顺手把一次归属迁移也一起点了，而那件事他根本没看。**
 *
 * 上一版就是这么调的（不传 `what`），也就是说**那个按钮此刻还在替他
 * 做一件他没看过的事**。所以这里把参数改成必填的联合类型：
 * **不是「建议传」，是编译期不给你不传的机会。**
 *
 * 只认一半时另一半的建议留着，不会被清掉（契约保证）。
 */
export function useAcceptSuggestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      hostId: string
      what: 'identity' | 'parent' | 'reject_standalone' | 'reject_keep'
    }) =>
      call('/hosts/{hostId}/suggestion/accept', () =>
        api.POST('/hosts/{hostId}/suggestion/accept', {
          params: { path: { hostId: input.hostId } },
          body: { what: input.what },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hosts })
      void queryClient.invalidateQueries({ queryKey: queryKeys.unassignedHosts })
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
    },
  })
}

/** 手动把一台机器归到一个项目。归属只影响视图和权限，不影响巡检。 */
export function useAssignHostToProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      hostId,
      projectId,
      role,
    }: {
      hostId: string
      projectId: string
      role?: string
    }) =>
      call('/hosts/{hostId}/projects', () =>
        api.POST('/hosts/{hostId}/projects', {
          params: { path: { hostId } },
          body: { project_id: projectId, role: role ?? null },
        }),
      ),
    onSuccess: (_host, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hosts })
      void queryClient.invalidateQueries({ queryKey: queryKeys.unassignedHosts })
      void queryClient.invalidateQueries({ queryKey: queryKeys.host(variables.hostId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
    },
  })
}

// ------------------------------------------------------------ 项目与总览

export function useProjects() {
  return useQuery({
    queryKey: queryKeys.projects,
    queryFn: async () => call('/projects', () => api.GET('/projects')),
  })
}

export function useProjectsSummary() {
  return useQuery({
    queryKey: queryKeys.projectsSummary,
    queryFn: async () => call('/projects/summary', () => api.GET('/projects/summary')),
  })
}

export function useInventoryResources() {
  return useQuery({
    queryKey: ['resources'] as const,
    queryFn: async () => call('/resources', () => api.GET('/resources')),
  })
}

export function useCompleteHostOnboarding(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (hostId: string) => call('/projects/{projectId}/resources/{hostId}/complete-onboarding', () =>
      api.POST('/projects/{projectId}/resources/{hostId}/complete-onboarding', { params: { path: { projectId, hostId } } })),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.projectResources(projectId) }),
  })
}

export function useProjectResources(projectId: string) {
  return useQuery({
    queryKey: queryKeys.projectResources(projectId),
    enabled: projectId.length > 0,
    refetchInterval: 10000,
    queryFn: async () =>
      call('/projects/{projectId}/resources', () =>
        api.GET('/projects/{projectId}/resources', { params: { path: { projectId } } }),
      ),
  })
}

export function useProjectEnvironments(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'environments'] as const,
    enabled: projectId.length > 0,
    queryFn: async () =>
      call('/projects/{projectId}/environments', () =>
        api.GET('/projects/{projectId}/environments', { params: { path: { projectId } } }),
      ),
  })
}

export function useConfigureProjectEnvironment(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      domain: string
      environmentKind: 'test' | 'production'
      hostId: string
      files: File[]
    }) => {
      const form = new FormData()
      form.append('domain', input.domain.trim())
      form.append('environment_kind', input.environmentKind)
      form.append('host_id', input.hostId)
      for (const file of input.files) form.append('files', file, file.name)
      return call('/projects/{projectId}/environment-configuration', () =>
        api.POST('/projects/{projectId}/environment-configuration', {
          params: { path: { projectId } },
          body: form as unknown as {
            domain: string
            environment_kind: 'test' | 'production'
            host_id: string
            files: string[]
          },
          bodySerializer: (body: unknown) => body as BodyInit,
        }),
      )
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.project(projectId) })
      void client.invalidateQueries({ queryKey: queryKeys.projectResources(projectId) })
      void client.invalidateQueries({ queryKey: ['projects', projectId, 'environments'] })
    },
  })
}

export function useProject(projectId: string) {
  return useQuery({
    queryKey: queryKeys.project(projectId),
    enabled: projectId.length > 0,
    queryFn: async () =>
      call('/projects/{projectId}', () =>
        api.GET('/projects/{projectId}', { params: { path: { projectId } } }),
      ),
  })
}

/**
 * 核对页读的那条。
 *
 * **等资料包的时候要自己去看一眼。** 交包的不是坐在这一屏前面的人，是原来那个
 * 开发 AI——它什么时候交、交没交，这一屏收不到任何通知。不轮询的话，
 * 用户把交代词发出去、盯着这一屏，而它会一直停在「还在等原来的开发 AI 交资料包」，
 * 哪怕包早就到了。他唯一的出路是手动刷新，而没有任何地方告诉他要这么做。
 */
export function useProjectImport(importId: string) {
  return useQuery({
    queryKey: queryKeys.projectImport(importId),
    queryFn: () => getProjectImport(importId),
    enabled: importId.length > 0,
    refetchInterval: (query) =>
      importPollInterval(query.state.data?.status, query.state.data?.source_import.status),
  })
}

/**
 * 等资料包的时候每 3 秒看一眼，收到就停。
 *
 * 抽成具名函数是为了让它**有人守**：写成 useQuery 里的一个内联箭头函数，
 * 「收到之后要停」这句话就没有任何用例撑着，而它坏掉的样子是一条永远在轮的
 * 查询——那种东西不报错，只是一直在跑。
 */
export function importPollInterval(
  status: ProjectImport['status'] | undefined,
  sourceStatus: ProjectImport['source_import']['status'] | undefined,
): number | false {
  const sourcePending =
    sourceStatus !== undefined && ['awaiting_source', 'importing', 'status_unavailable'].includes(sourceStatus)
  return status === 'awaiting_package' || sourcePending ? 3000 : false
}

/**
 * 项目页那张导入卡读的就是这条。
 *
 * 没有导入草稿的普通项目会拿到 200 + null；查询成功且没有数据，卡片不出现。
 * retry 仍然关掉，避免项目概览上的附属信息拖慢主界面。
 */
export function useProjectImportByProject(projectId: string) {
  return useQuery({
    queryKey: queryKeys.projectImportByProject(projectId),
    queryFn: () => getProjectImportByProject(projectId),
    enabled: projectId.length > 0,
    retry: false,
  })
}

export function useCreateProjectImport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ProjectImportCreate & { idempotencyKey?: string }) => {
      const { idempotencyKey, ...body } = input
      return createProjectImport(body, idempotencyKey)
    },
    onSuccess: (created) => {
      queryClient.setQueryData(queryKeys.projectImport(created.import.id), created.import)
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects })
    },
  })
}

export function useApproveProjectImport(importId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (claimKeys: string[]) => approveProjectImport(importId, claimKeys),
    onSuccess: (item) => settleProjectImport(queryClient, item),
  })
}

export function useUnlinkProjectImport(importId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ProjectImportUnlink) => unlinkProjectImport(importId, input),
    onSuccess: (item) => settleProjectImport(queryClient, item),
  })
}

export function useReissueProjectImportPackage(importId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => reissueProjectImportPackage(importId),
    onSuccess: (created) => settleProjectImport(queryClient, created.import),
  })
}

/**
 * 批准与撤销落地之后要刷同一批东西。
 *
 * 收成一处不是为了少写几行：**这两个动作改的是同一份事实**——项目上挂了哪些
 * 机器、哪些服务商、续费到什么时候。各写各的刷新列表，迟早会有一边少刷一个，
 * 而少刷的表现是撤销之后项目页还挂着那台机器，看着像撤销没生效。
 */
function settleProjectImport(queryClient: ReturnType<typeof useQueryClient>, item: ProjectImport) {
  queryClient.setQueryData(queryKeys.projectImport(item.id), item)
  queryClient.setQueryData(queryKeys.projectImportByProject(item.project_id), item)
  void queryClient.invalidateQueries({ queryKey: queryKeys.projects })
  void queryClient.invalidateQueries({ queryKey: queryKeys.project(item.project_id) })
  void queryClient.invalidateQueries({ queryKey: queryKeys.projectHosts(item.project_id) })
  void queryClient.invalidateQueries({ queryKey: queryKeys.vendors })
}

// ------------------------------------------------------------- 开发任务

export function useDevelopmentTaskOptions(projectId: string) {
  return useQuery({
    queryKey: queryKeys.developmentTaskOptions(projectId),
    queryFn: async () => getDevelopmentTaskOptions(projectId),
  })
}

export function useProjectRepositories(projectId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.projectRepositories(projectId),
    queryFn: async () => listProjectRepositories(projectId),
    enabled,
  })
}

export function useProjectRepositoryCandidates(projectId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.projectRepositoryCandidates(projectId),
    queryFn: async () => listProjectRepositoryCandidates(projectId),
    enabled,
  })
}

export function useLinkProjectRepository(projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: ProjectRepositoryLinkRequest) =>
      linkProjectRepository(projectId, input),
    onSuccess: async (result) => {
      queryClient.setQueryData<ProjectRepositories>(
        queryKeys.projectRepositories(projectId),
        (current) => ({
          repositories: [
            ...(current?.repositories.filter(
              (repository) => repository.id !== result.repository.id,
            ) ?? []),
            result.repository,
          ],
        }),
      )
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.projectRepositories(projectId) }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.projectRepositoryCandidates(projectId),
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.developmentTaskOptions(projectId) }),
      ])
    },
    onError: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.projectRepositories(projectId) }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.projectRepositoryCandidates(projectId),
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.developmentTaskOptions(projectId) }),
      ])
    },
  })
}

export function useCreateDevelopmentTask(projectId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateDevelopmentTaskInput) =>
      createDevelopmentTask(projectId, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.projectDevelopmentTasks(projectId),
      })
    },
  })
}

export function useProjectDevelopmentTasks(projectId: string, filter: DevelopmentTaskListQuery = {}) {
  return useQuery({
    queryKey: queryKeys.projectDevelopmentTasks(projectId, filter),
    queryFn: () => listProjectDevelopmentTasks(projectId, filter),
  })
}

export function useDevelopmentTask(taskId: string) {
  return useQuery({
    queryKey: queryKeys.developmentTask(taskId),
    queryFn: async () => getDevelopmentTask(taskId),
  })
}

export function useDevelopmentTaskEvents(taskId: string) {
  return useQuery({
    queryKey: queryKeys.developmentTaskEvents(taskId),
    queryFn: async () => getDevelopmentTaskEvents(taskId),
  })
}

export function usePrepareDevelopmentTask(taskId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: DevelopmentTaskPrepareInput = {}) =>
      prepareDevelopmentTask(taskId, input),
    onSuccess: async (task) => {
      queryClient.setQueryData(queryKeys.developmentTask(taskId), task)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.developmentTask(taskId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.developmentTaskEvents(taskId) }),
      ])
    },
  })
}

/**
 * 交一次预览。成功之后回执自己就是当前状态，所以这里只把任务和时间线拉新一遍，
 * 不去猜任务会变成什么——那是服务端的事。
 *
 * 回执同时写入 preview-receipt 缓存。刷新后走 GET latest，不要编回执。
 */
export function useRequestDevelopmentTaskPreview(projectId: string, taskId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (sha: string) => requestDevelopmentTaskPreview(projectId, taskId, sha),
    onSuccess: async (receipt) => {
      rememberPreviewReceipt(queryClient, taskId, receipt)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.developmentTask(taskId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.developmentTaskEvents(taskId) }),
      ])
    },
  })
}

export function useCancelDevelopmentTask(taskId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => cancelDevelopmentTask(taskId),
    onSuccess: (task) => {
      queryClient.setQueryData(queryKeys.developmentTask(taskId), task)
      void queryClient.invalidateQueries({ queryKey: queryKeys.developmentTaskEvents(taskId) })
    },
  })
}

export function useDevelopmentTaskComments(taskId: string) {
  return useQuery({
    queryKey: [...queryKeys.developmentTask(taskId), 'comments'] as const,
    queryFn: async () => listDevelopmentTaskComments(taskId),
  })
}

export function useAddDevelopmentTaskComment(taskId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: string) => addDevelopmentTaskComment(taskId, body),
    onSuccess: (data) => {
      queryClient.setQueryData([...queryKeys.developmentTask(taskId), 'comments'], data)
      // 评论可能把任务重开一轮：任务本身与时间线一起重取。
      void queryClient.invalidateQueries({ queryKey: queryKeys.developmentTask(taskId) })
    },
  })
}

export function useClaimDevelopmentTask(taskId: string) {
  const queryClient = useQueryClient()
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function claim(executorId: string, executorIdentity?: DevelopmentTaskExecutorIdentity) {
    setIsPending(true)
    setError(null)
    try {
      // A grant contains a one-time capability. The modal alone owns this raw response.
      const result = await claimDevelopmentTask(taskId, executorId, executorIdentity)
      queryClient.setQueryData(queryKeys.developmentTask(taskId), result.task)
      void queryClient.invalidateQueries({ queryKey: queryKeys.developmentTaskEvents(taskId) })
      return result
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : render('error.generic'))
      throw cause
    } finally {
      setIsPending(false)
    }
  }

  return { claim, isPending, error, reset: () => setError(null) }
}

// ----------------------------------------------- AI Brain 远程开发与代码审核

/** Lulu 主模型有账户默认和项目覆盖，和开发偏好、补丁生成、代码审核彼此独立。 */
export function useAIStewardSetting() {
  return useQuery({
    queryKey: queryKeys.aiSteward,
    queryFn: async () =>
      call('/ai-brain/settings/steward', () => api.GET('/ai-brain/settings/steward')),
  })
}

export function usePutAIStewardSetting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (model: Schemas['AIStewardModel']) =>
      call('/ai-brain/settings/steward', () =>
        api.PUT('/ai-brain/settings/steward', {
          body: { provider: 'ark-monthly', model },
        }),
      ),
    onSuccess: (setting) => {
      queryClient.setQueryData(queryKeys.aiSteward, setting)
    },
  })
}

export function useProjectAIStewardSettings(projectId: string) {
  return useQuery({
    queryKey: queryKeys.aiStewardProject(projectId),
    queryFn: async () =>
      call('/projects/{projectId}/ai-brain/settings/steward', () =>
        api.GET('/projects/{projectId}/ai-brain/settings/steward', {
          params: { path: { projectId } },
        }),
      ),
  })
}

export function usePutProjectAIStewardSetting(projectId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (model: Schemas['AIStewardModel']) =>
      call('/projects/{projectId}/ai-brain/settings/steward', () =>
        api.PUT('/projects/{projectId}/ai-brain/settings/steward', {
          params: { path: { projectId } },
          body: { provider: 'ark-monthly', model },
        }),
      ),
    onSuccess: (settings) =>
      queryClient.setQueryData(queryKeys.aiStewardProject(projectId), settings),
  })
}

export function useDeleteProjectAIStewardSetting(projectId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      call('/projects/{projectId}/ai-brain/settings/steward', () =>
        api.DELETE('/projects/{projectId}/ai-brain/settings/steward', {
          params: { path: { projectId } },
        }),
      ),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.aiStewardProject(projectId) }),
  })
}

export function useAIPatchGeneratorSettings(projectId?: string) {
  return useQuery({
    queryKey: queryKeys.aiPatchGenerator(projectId),
    queryFn: async () =>
      call('/ai-brain/settings/patch-generator', () =>
        api.GET('/ai-brain/settings/patch-generator', {
          params: { query: { project_id: projectId } },
        }),
      ),
  })
}

export function usePutGlobalAIPatchGeneratorSetting() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (model: Schemas['AIPatchGeneratorModel']) =>
      call('/ai-brain/settings/patch-generator', () =>
        api.PUT('/ai-brain/settings/patch-generator', {
          body: { provider: 'ark-monthly', model },
        }),
      ),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.aiPatchGenerator(), settings)
      void queryClient.invalidateQueries({ queryKey: queryKeys.aiPatchGeneratorRoot })
    },
  })
}

export function usePutProjectAIPatchGeneratorSetting(projectId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (model: Schemas['AIPatchGeneratorModel']) =>
      call('/projects/{projectId}/ai-brain/settings/patch-generator', () =>
        api.PUT('/projects/{projectId}/ai-brain/settings/patch-generator', {
          params: { path: { projectId } },
          body: { provider: 'ark-monthly', model },
        }),
      ),
    onSuccess: (settings) =>
      queryClient.setQueryData(queryKeys.aiPatchGenerator(projectId), settings),
  })
}

export function useDeleteProjectAIPatchGeneratorSetting(projectId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      call('/projects/{projectId}/ai-brain/settings/patch-generator', () =>
        api.DELETE('/projects/{projectId}/ai-brain/settings/patch-generator', {
          params: { path: { projectId } },
        }),
      ),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.aiPatchGenerator(projectId) }),
  })
}

export function useAIDeveloperSettings() {
  return useQuery({
    queryKey: queryKeys.aiDeveloper,
    queryFn: async () =>
      call('/ai-brain/settings/developer', () => api.GET('/ai-brain/settings/developer')),
  })
}

export function usePutAIDeveloperSetting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (model: Schemas['AIDeveloperModel']) =>
      call('/ai-brain/settings/developer', () =>
        api.PUT('/ai-brain/settings/developer', {
          body: { provider: 'ark-monthly', model },
        }),
      ),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.aiDeveloper, settings)
    },
  })
}

export function useAIReviewerSettings(projectId?: string) {
  return useQuery({
    queryKey: queryKeys.aiReviewer(projectId),
    queryFn: async () =>
      call('/ai-brain/settings/reviewer', () =>
        api.GET('/ai-brain/settings/reviewer', {
          params: { query: { project_id: projectId } },
        }),
      ),
  })
}

export function usePutGlobalAIReviewerSetting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (model: Schemas['AIReviewerModel']) =>
      call('/ai-brain/settings/reviewer', () =>
        api.PUT('/ai-brain/settings/reviewer', {
          body: { provider: 'ark-monthly', model },
        }),
      ),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.aiReviewer(), settings)
      void queryClient.invalidateQueries({ queryKey: queryKeys.aiReviewerRoot })
    },
  })
}

export function usePutProjectAIReviewerSetting(projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (model: Schemas['AIReviewerModel']) =>
      call('/projects/{projectId}/ai-brain/settings/reviewer', () =>
        api.PUT('/projects/{projectId}/ai-brain/settings/reviewer', {
          params: { path: { projectId } },
          body: { provider: 'ark-monthly', model },
        }),
      ),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.aiReviewer(projectId), settings)
    },
  })
}

export function useDeleteProjectAIReviewerSetting(projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () =>
      call('/projects/{projectId}/ai-brain/settings/reviewer', () =>
        api.DELETE('/projects/{projectId}/ai-brain/settings/reviewer', {
          params: { path: { projectId } },
        }),
      ),
    onSuccess: () => {
      queryClient.setQueryData<Schemas['AIReviewerSettings']>(
        queryKeys.aiReviewer(projectId),
        (settings) =>
          settings
            ? {
                ...settings,
                project: null,
                effective: {
                  provider: settings.global.provider,
                  model: settings.global.model,
                  source: 'global',
                },
              }
            : settings,
      )
    },
  })
}

/** 这个项目住在哪些机器上。列表接口按 `project_id` 过滤，没有单独的子资源。 */
export function useProjectHosts(projectId: string) {
  return useQuery({
    queryKey: queryKeys.projectHosts(projectId),
    queryFn: async () =>
      call('/hosts', () => api.GET('/hosts', { params: { query: { project_id: projectId } } })),
  })
}

/** 项目详情页「给它找台机器」：从还没归项目池里挑一台挂到当前项目。 */
export function useAssignProjectHost(projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (hostId: string) =>
      call('/projects/{projectId}/hosts', () =>
        api.POST('/projects/{projectId}/hosts', {
          params: { path: { projectId } },
          body: { host_id: hostId },
        }),
      ),
    onSuccess: (_assignment, hostId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hosts })
      void queryClient.invalidateQueries({ queryKey: queryKeys.unassignedHosts })
      void queryClient.invalidateQueries({ queryKey: queryKeys.host(hostId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.project(projectId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectHosts(projectId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectResources(projectId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectsSummary })
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
    },
  })
}


/** 项目资源行上「卸下」：归属回到空闲池，巡检不停。 */
export function useUnassignProjectHost(projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (hostId: string) =>
      call('/hosts/{hostId}/projects/{projectId}', () =>
        api.DELETE('/hosts/{hostId}/projects/{projectId}', {
          params: { path: { hostId, projectId } },
        }),
      ),
    onSuccess: (_result, hostId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hosts })
      void queryClient.invalidateQueries({ queryKey: queryKeys.unassignedHosts })
      void queryClient.invalidateQueries({ queryKey: queryKeys.host(hostId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.project(projectId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectHosts(projectId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectResources(projectId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectsSummary })
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
    },
  })
}

export function useDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: async () => call('/dashboard', () => api.GET('/dashboard')),
  })
}

export function useToday() {
  return useQuery({
    queryKey: queryKeys.today,
    queryFn: async () => call('/today', () => api.GET('/today')),
    refetchInterval: 15_000,
  })
}

// -------------------------------------------------------------- 应用发布

export function useProjectFastDeliveries(projectId: string) {
  return useQuery({
    queryKey: queryKeys.projectFastDeliveries(projectId),
    queryFn: () => listProjectFastDeliveries(projectId),
  })
}

export function useProjectFastDelivery(projectId: string, releaseId: string | null) {
  return useQuery({
    queryKey: queryKeys.projectFastDelivery(projectId, releaseId ?? ''),
    queryFn: () => getProjectFastDelivery(projectId, releaseId as string),
    enabled: Boolean(releaseId),
  })
}

// -------------------------------------------------------------- 发个网页

/**
 * HTML 快速发布。项目站按 projectId，公共站按账户。
 *
 * 幂等键在**每次 mutate 时生成一次**（`newIdempotencyKey()`），
 * TanStack Query 的自动重试沿用同一次 `mutationFn` 调用里的那个键——
 * 换个写法在这一层每次请求前现取一个，重试就等于新开一个操作，
 * 而幂等失效的样子和正常一模一样。
 */
export function useStaticSitesFor(scope: StaticPublishScope) {
  return useQuery({
    queryKey:
      scope.kind === 'account' ? queryKeys.accountStaticSites : queryKeys.staticSites(scope.projectId),
    queryFn: () => listStaticSitesFor(scope),
  })
}

export function useStaticSites(projectId: string) {
  return useStaticSitesFor({ kind: 'project', projectId })
}

export function useProjectStaticSiteSummary(projectId: string) {
  return useQuery({
    queryKey: queryKeys.staticSiteSummary(projectId),
    queryFn: () => getProjectStaticSiteSummary(projectId),
  })
}

export function useStaticSiteVersionsFor(scope: StaticPublishScope, siteId: string | null) {
  return useQuery({
    queryKey:
      scope.kind === 'account'
        ? queryKeys.accountStaticSiteVersions(siteId ?? '')
        : queryKeys.staticSiteVersions(scope.projectId, siteId ?? ''),
    queryFn: () => listStaticSiteVersionsFor(scope, siteId as string),
    enabled: Boolean(siteId),
  })
}

export function useStaticSiteVersions(projectId: string, siteId: string | null) {
  return useStaticSiteVersionsFor({ kind: 'project', projectId }, siteId)
}

export function useStaticSiteDeploymentsFor(scope: StaticPublishScope, siteId: string | null) {
  return useQuery({
    queryKey:
      scope.kind === 'account'
        ? queryKeys.accountStaticSiteDeployments(siteId ?? '')
        : queryKeys.staticSiteDeployments(scope.projectId, siteId ?? ''),
    queryFn: () => listStaticSiteDeploymentsFor(scope, siteId as string),
    enabled: Boolean(siteId),
    refetchInterval: (query) => staticSiteDeploymentsRefetchMs(query.state.data),
  })
}

export function useStaticSiteDeployments(projectId: string, siteId: string | null) {
  return useStaticSiteDeploymentsFor({ kind: 'project', projectId }, siteId)
}

export function useCreateStaticSite(projectId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { slug: string; title?: string }) =>
      createStaticSite(projectId, input, newIdempotencyKey()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.staticSites(projectId) })
    },
  })
}

/**
 * 改项目显示名。
 *
 * **只改元数据，不动已经发出去的域名。** 首次发布时域名由当时的项目名与
 * 项目 ID 定下来，之后改名不迁移、不重定向——那条 URL 可能已经被用户发出去了。
 * 界面上要把「固定 URL」与「显示名」分开讲清楚（裁决，2026-08-23）。
 */
export function useRenameProject(projectId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (name: string) =>
      call('/projects/{projectId}', () =>
        api.PATCH('/projects/{projectId}', {
          params: { path: { projectId } },
          body: { name },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.project(projectId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects })
    },
  })
}

/**
 * 登录用户的直接发布。拖一份进来就发；同站更新带 `siteId`，另建站点带 `newSite`。
 * 成功后站点列表、汇总、版本和部署历史都变了，一起重取。
 */
export function useFastPublishStaticSiteFor(scope: StaticPublishScope) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { file: Blob; siteId?: string; newSite?: boolean }) =>
      fastPublishStaticSiteFor(scope, input.file, newIdempotencyKey(), {
        ...(input.newSite ? { newSite: true } : input.siteId ? { siteId: input.siteId } : {}),
      }),
    onSuccess: (result) => {
      if (scope.kind === 'account') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.accountStaticSites })
        void queryClient.invalidateQueries({
          queryKey: queryKeys.accountStaticSiteVersions(result.site.id),
        })
        void queryClient.invalidateQueries({
          queryKey: queryKeys.accountStaticSiteDeployments(result.site.id),
        })
        return
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.staticSites(scope.projectId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.staticSiteSummary(scope.projectId) })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.staticSiteVersions(scope.projectId, result.site.id),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.staticSiteDeployments(scope.projectId, result.site.id),
      })
    },
  })
}

export function useFastPublishStaticSite(projectId: string) {
  return useFastPublishStaticSiteFor({ kind: 'project', projectId })
}

/** 登录用户的直接回滚。不要批准码。 */
export function useFastRollbackStaticSiteFor(scope: StaticPublishScope, siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (versionId: string) =>
      fastRollbackStaticSiteFor(scope, siteId, versionId, newIdempotencyKey()),
    onSuccess: () => {
      if (scope.kind === 'account') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.accountStaticSites })
        void queryClient.invalidateQueries({
          queryKey: queryKeys.accountStaticSiteVersions(siteId),
        })
        void queryClient.invalidateQueries({
          queryKey: queryKeys.accountStaticSiteDeployments(siteId),
        })
        return
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.staticSites(scope.projectId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.staticSiteSummary(scope.projectId) })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.staticSiteVersions(scope.projectId, siteId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.staticSiteDeployments(scope.projectId, siteId),
      })
    },
  })
}

export function useFastRollbackStaticSite(projectId: string, siteId: string) {
  return useFastRollbackStaticSiteFor({ kind: 'project', projectId }, siteId)
}

export function useUploadStaticSiteVersion(projectId: string, siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (file: Blob) =>
      uploadStaticSiteVersion(projectId, siteId, file, newIdempotencyKey()),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.staticSiteVersions(projectId, siteId),
      })
      // 上传会顺带开一条预览链接，所以那份列表也变了。不重取的话，
      // 「先看一眼」那一块会一直显示上一次的状态。
      void queryClient.invalidateQueries({
        queryKey: queryKeys.staticSitePreviews(projectId, siteId),
      })
    },
  })
}

export function useStaticSitePreviews(projectId: string, siteId: string | null) {
  return useQuery({
    queryKey: queryKeys.staticSitePreviews(projectId, siteId ?? ''),
    queryFn: () => listStaticSitePreviews(projectId, siteId as string),
    enabled: Boolean(siteId),
  })
}

export function useCreateStaticSitePreview(projectId: string, siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (versionId: string) => createStaticSitePreview(projectId, siteId, versionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.staticSitePreviews(projectId, siteId),
      })
    },
  })
}

export function useRevokeStaticSitePreview(projectId: string, siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (previewId: string) => revokeStaticSitePreview(projectId, siteId, previewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.staticSitePreviews(projectId, siteId),
      })
    },
  })
}

/**
 * 放出去。
 *
 * 成功之后三处都要重取，因为它们**三个都变了**：站点的 `row_version` 变了
 * （下一次发布要用新的那个，用旧的是 409）、版本的 status 变了、
 * 部署历史多了一条。只重取部署历史的话，界面上看着发布成功了，
 * 而下一次发布必然 409 且看不出为什么。
 */
export function usePublishStaticSite(projectId: string, siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      versionId: string
      expectedRowVersion: number
      reason: string
      signedAction: string
    }) => publishStaticSite(projectId, siteId, input, newIdempotencyKey()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.staticSites(projectId) })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.staticSiteVersions(projectId, siteId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.staticSiteDeployments(projectId, siteId),
      })
    },
  })
}

/**
 * 去要一张平台批准码。
 *
 * 三步一次做完，因为对用户来说它就是一件事（「让平台批一下」）：
 *
 *  1. `POST …/approvals` 开一条待批请求，拿到它的 `action_id`
 *  2. 用 **批红级那一类** 的 intent 换一次二次验证：
 *     `/auth/step-up/options` → 系统指纹弹窗 → `/auth/step-up/verify`。
 *     `resource_id` 是那条 `action_id`——令牌绑定到具体这一条，
 *     拿它去批别的动作会被服务端拒掉（否则二次验证就退化成
 *     「随便验一次就放行十分钟」，而钓鱼场景恰恰是骗人为一件小事按指纹）
 *  3. `POST /actions/{actionId}/approve` 带上那张令牌，服务端在**同一步**
 *     用平台私钥签出批准码并回给我们
 *
 * # 这里没有一行是在签名
 *
 * 浏览器拿到的是一串已经签好的 base64：它既造不出第二张，也改不动手上这一张
 * （改一个字节验签就不过）。**刻意不做「前端拿不到码就自动放行」**——
 * 一条能被制造出来的旁路等于没有门（CLAUDE.md §8）。
 *
 * # 为什么不 invalidate 任何东西
 *
 * 这一步没有改变任何列表：站点、版本、部署历史一样都没动。
 * 它产出的是一张**只在调用方手上**的码，交给 `PublishForm` 的 state。
 * 顺手 invalidate 一圈会让那张卡整个重画，而重画会把用户刚填的
 * reason 连同这张码一起冲掉。
 */
export function useRequestStaticSiteApproval(projectId: string, siteId: string) {
  return useMutation({
    mutationFn: async (input: {
      op: 'publish' | 'rollback' | 'suspend' | 'resume' | 'offline'
      versionId?: string
      reason: string
      /** 红级审批的二次验证：当前密码（由调用方弹窗收好传入）。 */
      stepUpPassword: string
    }): Promise<StaticSiteApprovalGrant> => {
      const pending = await requestStaticSiteApproval(projectId, siteId, {
        op: input.op,
        ...(input.versionId ? { version_id: input.versionId } : {}),
        reason: input.reason,
      })
      // Passkey 退役后的红级审批确认：当前密码换一次性令牌（新钥匙之一）。
      // 密码由调用方在弹窗里收好传入；不知道密码的人批不了红级。
      const password = input.stepUpPassword
      if (!password) throw new Error('step-up password required')
      const ticket = await call('/auth/step-up/password', () =>
        api.POST('/auth/step-up/password', {
          body: { password, intent: 'approve_red_action', resource: pending.action_id },
        }),
      )
      return approveStaticSiteAction(pending.action_id, ticket.token)
    },
  })
}

// ------------------------------------------------------------ 代码与发布

/**
 * 等着合的改动 + 最近被打回的。**不按项目分。**
 *
 * `code` 屏看的是「你的代码」整体——设计稿那一屏没有项目选择器。
 * 上一版只能调按项目取的那个端点、然后临时取第一个非内置项目，
 * 于是**漏掉其余所有项目的推送，而界面上完全看不出漏了**。
 * 流 0 为此专门加了这条不按项目分的端点（契约里那段说明写着同一件事）。
 *
 * 按项目取的那个端点还在，那是给**本地 AI** 用的：它开工前只关心自己那个项目。
 */
export function usePendingChanges() {
  return useQuery({
    queryKey: ['pending-changes'] as const,
    queryFn: async () => call('/code/pending-changes', () => api.GET('/code/pending-changes')),
  })
}

// ------------------------------------------------------------ 服务商与账号

export function useVendors() {
  return useQuery({
    queryKey: queryKeys.vendors,
    queryFn: async () => call('/vendors', () => api.GET('/vendors')),
  })
}

export function useUpsertVendorProjectLink(vendorId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: Schemas['VendorProjectLinkWrite']) =>
      call('/vendors/{vendorId}/project-links', () =>
        api.POST('/vendors/{vendorId}/project-links', {
          params: { path: { vendorId } },
          body: input,
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.vendors })
    },
  })
}

export function useDeleteVendorPublicProjectLink(vendorId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () =>
      call('/vendors/{vendorId}/project-links/public', () =>
        api.DELETE('/vendors/{vendorId}/project-links/public', {
          params: { path: { vendorId } },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.vendors })
    },
  })
}

export function useDeleteVendorProjectLink(vendorId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (projectId: string) =>
      call('/vendors/{vendorId}/project-links/projects/{projectId}', () =>
        api.DELETE('/vendors/{vendorId}/project-links/projects/{projectId}', {
          params: { path: { vendorId, projectId } },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.vendors })
    },
  })
}

// ---------------------------------------------------------------- 一起管

/**
 * 名册与三档身份的说明。
 *
 * `roles` 里每一档的中文名与说明**是后端按 `docs/copy/team.yaml` 渲染好的**，
 * 前端不再拿 `team.role.owner` 自己渲染一遍——契约两处都写了同一句担心：
 * 同一个角色在两屏上迟早出现两种说法。
 */
export function useTeam() {
  return useQuery({
    queryKey: ['team'] as const,
    queryFn: async () => call('/team', () => api.GET('/team')),
  })
}

/** 「我是谁、我能干什么」：能不能叫人进来看 capabilities 里有没有 manage_team，不看 role。 */
export function useTeamMe() {
  return useQuery({
    queryKey: ['team', 'me'] as const,
    queryFn: async () => call('/team/me', () => api.GET('/team/me')),
  })
}

/** 还挂着的邀请（没接、没撤、没过期）。 */
export function useInvitations() {  return useQuery({
    queryKey: ['team', 'invitations'] as const,
    queryFn: async () => call('/team/invitations', () => api.GET('/team/invitations')),
  })
}

/** 叫个人进来：要二次验证令牌（加人是改团队权限，CLAUDE.md §10 点名的四件之一）。 */
export function useCreateInvitation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      readonly email: string
      readonly role: Schemas['MemberRole']
      readonly projects: readonly string[]
      readonly stepUpToken: string
    }) =>
      call('/team/invitations', () =>
        api.POST('/team/invitations', {
          params: { header: { 'X-Npc-Step-Up': input.stepUpToken } },
          body: { email: input.email, role: input.role, projects: [...input.projects] },
        }),
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['team', 'invitations'] })    },
  })
}

export function useSetMemberRole() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      readonly userId: string
      readonly role: Schemas['MemberRole']
      readonly projects: readonly string[]
      readonly stepUpToken: string
    }) =>
      call('/team/members/{userId}', () =>
        api.PATCH('/team/members/{userId}', {
          params: { path: { userId: input.userId }, header: { 'X-Npc-Step-Up': input.stepUpToken } },
          body: { role: input.role, projects: [...input.projects] },
        }),
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['team'] })
    },
  })
}

export function useRevokeInvitation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (invitationId: string) =>
      call('/team/invitations/{invitationId}', () =>
        api.DELETE('/team/invitations/{invitationId}', { params: { path: { invitationId } } }),
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['team', 'invitations'] })
    },
  })
}

/** 被邀请的人拿链接里的 token 入队（已登录即可，2026-09-05 闭环补充）。 */
export function useRedeemInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (token: string) =>
      call('/team/invitations/redeem', () =>
        api.POST('/team/invitations/redeem', { body: { token } }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['team'] })
    },
  })
}

// ---------------------------------------------------------------- 备忘录

/**
 * 作用域（2026-08 设计稿 SPEC-routes-api §1）：
 * - 杂项：`/memos`，服务端强制 `project_id IS NULL`
 * - 项目内：`/projects/{projectId}/memos`，projectId 从路由来
 *
 * 没有第三种。前端不传 projectId 到 body——作用域由路由决定，
 * 这条规矩是防「把内容写进任意项目」的越界。
 */
export type MemoScope = { projectId?: string }

/** 三态筛选。「all」不过滤，其余按 `ai_access` 取值原样下传。 */
export type MemoAccessFilter = 'all' | 'default' | 'shared' | 'hidden'

export function useMemos(scope: MemoScope, access: MemoAccessFilter = 'all') {
  return useQuery({
    queryKey: ['memos', scope.projectId ?? 'global', access] as const,
    // 切筛选档时保住上一档的条目：整屏闪回骨架，用户会把一次普通的
    // 切档读成「列表没了」。旧数据在，新数据到了原位换掉。
    placeholderData: keepPreviousData,
    queryFn: async () =>
      scope.projectId
        ? call('/projects/{projectId}/memos', () =>
            api.GET('/projects/{projectId}/memos', {
              params: {
                path: { projectId: scope.projectId! },
                query: access === 'all' ? {} : { ai_access: access },
              },
            }),
          )
        : call('/memos', () =>
            api.GET('/memos', {
              params: { query: access === 'all' ? {} : { ai_access: access } },
            }),
          ),
  })
}

/**
 * 输密码解锁一条（用户需求：单条可设密码，默认不设）。
 * 对了回完整的一条（正文/附件/原声）；不对抛带人话的错。
 * 密码只进请求体——不进查询参数，不进访问日志。
 */
export function unlockMemo(memoId: string, password: string) {
  return call('/memos/{memoId}/unlock', () =>
    api.POST('/memos/{memoId}/unlock', {
      params: { path: { memoId } },
      body: { password },
    }),
  )
}

/**
 * 直连创建（离线补传用，非 hook）：作用域跟着**队列条目**走——
 * 在项目页断网写的条目带那个项目的 id，回到杂项页补传也要落回项目。
 */
export async function createMemoDirect(input: { body: string; projectId?: string | null }) {
  return input.projectId
    ? call('/projects/{projectId}/memos', () =>
        api.POST('/projects/{projectId}/memos', {
          params: { path: { projectId: input.projectId! } },
          body: { body: input.body },
        }),
      )
    : call('/memos', () => api.POST('/memos', { body: { body: input.body } }))
}

/** 跨作用域搜索（R1-1）：杂项+看得到的项目一起，正文与附件名都算命中。 */
export function useSearchMemos(q: string, enabled: boolean) {
  return useQuery({
    queryKey: ['memos', 'search', q] as const,
    enabled: enabled && q.trim().length > 0,
    queryFn: async () =>
      call('/memos/search', () =>
        api.GET('/memos/search', { params: { query: { q } } }),
      ),
  })
}

/** 取一条（非 hook）：版本冲突时拿服务器上最新那份摆出来（S12）。 */
export function fetchMemo(memoId: string) {
  return call('/memos/{memoId}', () =>
    api.GET('/memos/{memoId}', { params: { path: { memoId } } }),
  )
}

/** 归档区（S18）：跨作用域混排，每条自带 project_id 画作用域标签。 */
export function useArchivedMemos() {
  return useQuery({
    queryKey: ['memos', 'archived'] as const,
    queryFn: async () => call('/memos/archived', () => api.GET('/memos/archived')),
  })
}

/**
 * 记一条。作用域决定打哪条路由：项目内走 `/projects/{id}/memos`
 * （服务端从路径绑定），杂项走 `/memos`（body 里就算带 project_id
 * 也不被采信）。
 *
 * **不传 `ai_access` 就是 `default`（都没标），不是 `shared`。**
 * 契约：「都没标」和「明确不给 AI 看」不是一回事，前端不许把它们压成一个。
 * 要开去详情里开（设计稿输入框里没有「AI 可读」开关，SPEC-scope-ai §2.3）。
 */
export function useCreateMemo(scope: MemoScope = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { body: string }) =>
      scope.projectId
        ? call('/projects/{projectId}/memos', () =>
            api.POST('/projects/{projectId}/memos', {
              params: { path: { projectId: scope.projectId! } },
              body: { body: input.body },
            }),
          )
        : call('/memos', () =>
            api.POST('/memos', {
              body: { body: input.body },
            }),
          ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['memos'] })
    },
  })
}

/** 一次改动的入参。expectedVersion 带上才拦得住并发（S12）。 */
export interface MemoUpdate {
  memoId: string
  body?: string
  aiAccess?: 'default' | 'shared' | 'hidden'
  /** 移动：null 表示移回杂项。跨作用域移动服务端会把 ai_access 重置成 default。 */
  projectId?: string | null
  /** 密码：字符串=设/改；null=去掉；不传=不动。默认不设。 */
  password?: string | null
  expectedVersion?: number
}

export function useUpdateMemo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: MemoUpdate) =>
      call('/memos/{memoId}', () =>
        api.PATCH('/memos/{memoId}', {
          params: { path: { memoId: input.memoId } },
          body: {
            ...(input.body !== undefined ? { body: input.body } : {}),
            ...(input.aiAccess !== undefined ? { ai_access: input.aiAccess } : {}),
            ...(input.password !== undefined ? { password: input.password } : {}),
            ...(input.projectId !== undefined
              ? { project_id: (input.projectId as string | null) ?? null }
              : {}),
            ...(input.expectedVersion !== undefined
              ? { expected_version: input.expectedVersion }
              : {}),
          },
        }),
      ),
    onSuccess: () => {
      // 筛选档、作用域、归档区全都撤——留着旧缓存会把刚改过/刚移动的
      // 条目显示在错误的作用域里，而「东西存到哪儿」正是这个模块
      // 最不能错的事。
      void queryClient.invalidateQueries({ queryKey: ['memos'] })
    },
  })
}

/**
 * 改一条的可见性三态。走 PATCH 的 `ai_access`——服务端对它单独记一条
 * `ai_access_change` 审计（把一条写着临时密码的备忘改成「让 AI 也读」，
 * 是少数一次点击就把内容送进模型上下文的事）。失败时这里不吞：
 * 调用方要保留原值并说人话。
 */
export function useUpdateMemoAccess() {
  const update = useUpdateMemo()
  return {
    ...update,
    mutate: (input: {
      memoId: string
      aiAccess: 'default' | 'shared' | 'hidden'
      expectedVersion: number
    }) =>
      update.mutate({
        memoId: input.memoId,
        aiAccess: input.aiAccess,
        expectedVersion: input.expectedVersion,
      }),
  }
}

/** 归档（不是删除）。 */
export function useArchiveMemo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (memoId: string) =>
      call('/memos/{memoId}', () =>
        api.DELETE('/memos/{memoId}', { params: { path: { memoId } } }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['memos'] })
    },
  })
}

/** 从归档拿回主列表（S18：一键撤销）。 */
export function useUnarchiveMemo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (memoId: string) =>
      call('/memos/{memoId}/unarchive', () =>
        api.POST('/memos/{memoId}/unarchive', { params: { path: { memoId } } }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['memos'] })
    },
  })
}

/**
 * AI 读取备忘的回执（最近的在前）。memo 屏底部那条常驻的
 * 「最近一次 AI 读取」用它，取第一条就是最近一次。
 */
export function useContextReads() {
  return useQuery({
    queryKey: ['memo-reads', 'recent'] as const,
    queryFn: async () =>
      call('/memos/context-reads', () =>
        api.GET('/memos/context-reads', { params: { query: { limit: 5 } } }),
      ),
  })
}

/**
 * 一条备忘被哪些读取包含过。`enabled` 由详情抽屉的展开态给——
 * 不展开就不取，回执是次级数据，不配常驻请求。
 */
export function useMemoReads(memoId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['memo-reads', memoId] as const,
    enabled,
    queryFn: async () =>
      call('/memos/{memoId}/context-reads', () =>
        api.GET('/memos/{memoId}/context-reads', {
          params: { path: { memoId } },
        }),
      ),
  })
}

/**
 * 记一条语音备忘（M3-60）。
 *
 * multipart 一步式：原声、时长、可见性一个请求建条目。**可见性必填**——
 * 文字路径可以「都没标」，语音路径服务端同样强制（ErrVoiceNeedsAccess），
 * 界面在选择框没选时就不放行，两道闸对着同一条规则。
 */
export function useCreateVoiceMemo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      blob: Blob
      mimeType: string
      durationMS: number
      aiAccess: 'default' | 'shared' | 'hidden'
      body?: string
      /** 作用域（设计稿硬约束 01）：项目内录音必须落进那个项目——
          之前一直没传，项目页录的语音全被后端当杂项存了。 */
      projectId?: string
    }) => {
      const form = new FormData()
      // 文件名按容器类型给后缀，后端认的是 Content-Type 不是名字
      form.append('audio', input.blob, input.mimeType.includes('mp4') ? 'memo.m4a' : 'memo.webm')
      form.append('duration_ms', String(input.durationMS))
      form.append('ai_access', input.aiAccess)
      if (input.projectId) form.append('project_id', input.projectId)
      if ((input.body ?? '').trim()) form.append('body', (input.body ?? '').trim())
      return call('/memos/audio', () =>
        api.POST('/memos/audio', {
          // 生成物把 multipart 体定型成 schema 对象；实际发的是 FormData，
          // bodySerializer 原样透传（openapi-fetch 不会给它 JSON.stringify，
          // 浏览器自己补带 boundary 的 content-type）
          body: form as unknown as {
            audio: string
            duration_ms: number
            ai_access: 'default' | 'shared' | 'hidden'
          },
          bodySerializer: (body: unknown) => body as BodyInit,
        }),
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['memos'] })
    },
  })
}

// ---------------------------------------------------------------- 附件

/**
 * 上传一个附件给已存在的备忘（SPEC-scope-ai §7）。
 *
 * 走 XHR 而不是生成客户端：**真实字节进度**只有 upload.onprogress 给得了，
 * 「上传中 42%」的那个 42 必须是浏览器数出来的，不是估的。进度不可得
 * （某些 WebView）时 onProgress 不回调，界面落到「上传中」无百分比——
 * 如实缺省，不造假数。
 *
 * 一个文件一个请求：一个文件一个状态（uploading|done|failed），
 * 失败单独重试，都建立在粒度上。
 */
export function uploadMemoAttachment(input: {
  memoId: string
  file: File
  onProgress?: (percent: number) => void
  signal?: AbortSignal
}): Promise<Schemas['MemoAttachment']> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_BASE}/memos/${input.memoId}/attachments`)
    xhr.responseType = 'json'

    const onAbort = () => xhr.abort()
    input.signal?.addEventListener('abort', onAbort, { once: true })

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        input.onProgress?.(Math.round((event.loaded / event.total) * 100))
      }
    }
    xhr.onload = () => {
      input.signal?.removeEventListener('abort', onAbort)
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response as Schemas['MemoAttachment'])
        return
      }
      reject(new Error(errorText(xhr.response, xhr.status)))
    }
    xhr.onerror = () => {
      input.signal?.removeEventListener('abort', onAbort)
      reject(new Error('网络错误，文件没传上去'))
    }
    xhr.onabort = () => {
      input.signal?.removeEventListener('abort', onAbort)
      reject(new DOMException('上传已取消', 'AbortError'))
    }

    const form = new FormData()
    form.append('file', input.file)
    xhr.send(form)
  })
}

/** 生成客户端的错误是 {error:{message}}；XHR 这边自己解一遍同构的形状。 */
function errorText(body: unknown, status: number): string {
  const message =
    body && typeof body === 'object'
      ? String((body as { error?: { message?: string } }).error?.message ?? '')
      : ''
  return message || `服务器返回了 ${status}`
}

/** 摘掉一个附件。条目本身不动——删的是这一个文件。 */
export function useDeleteMemoAttachment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { memoId: string; attachmentId: string }) =>
      call('/memos/{memoId}/attachments/{attachmentId}', () =>
        api.DELETE('/memos/{memoId}/attachments/{attachmentId}', {
          params: { path: { memoId: input.memoId, attachmentId: input.attachmentId } },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['memos'] })
    },
  })
}

// ---------------------------------------------------------------- 记忆候选

/** Lulu 建议记录（W14）。pending 是默认档——界面上只关心还没决定的。 */
export function useMemoryCandidates(status: 'pending' | 'confirmed' | 'discarded' = 'pending') {
  return useQuery({
    queryKey: ['memo-candidates', status] as const,
    queryFn: async () =>
      call('/memos/memory-candidates', () =>
        api.GET('/memos/memory-candidates', { params: { query: { status } } }),
      ),
  })
}

/**
 * 确认一条建议：服务端同一个事务里建出 Memo 并回链。
 * 可见性由确认的人终选——不传 ai_access 就用 Lulu 的提议值。
 */
export function useConfirmMemoryCandidate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { candidateId: string; aiAccess: 'default' | 'shared' | 'hidden' }) =>
      call('/memos/memory-candidates/{candidateId}/confirm', () =>
        api.POST('/memos/memory-candidates/{candidateId}/confirm', {
          params: { path: { candidateId: input.candidateId } },
          body: { ai_access: input.aiAccess },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['memo-candidates'] })
      void queryClient.invalidateQueries({ queryKey: ['memos'] })
    },
  })
}

/** 丢弃一条建议：留痕，不再出现在待决定列表。 */
export function useDiscardMemoryCandidate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (candidateId: string) =>
      call('/memos/memory-candidates/{candidateId}/discard', () =>
        api.POST('/memos/memory-candidates/{candidateId}/discard', {
          params: { path: { candidateId } },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['memo-candidates'] })
    },
  })
}

// -------------------------------------------------------- 出事的时候

/**
 * 告警。
 *
 * 不传 `state` 就是全部。设计稿那一屏用它喂手机 mockup——
 * **那不是一个告警列表**，是「一条通知长什么样」的样例，所以只用到最近一条。
 */
export function useAlerts(state?: 'open' | 'acknowledged' | 'resolved') {
  return useQuery({
    queryKey: ['alerts', state ?? 'all'] as const,
    queryFn: async () =>
      call('/alerts', () => api.GET('/alerts', { params: { query: { state } } })),
  })
}

/**
 * 把一条打开的运维告警标成「这条我看过了」。
 * 动态中心 `alerts=1` 收件箱用，不是那张手机 mockup。
 */
export function useAcknowledgeAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (alertId: string) =>
      call('/alerts/{alertId}/acknowledge', () =>
        api.POST('/alerts/{alertId}/acknowledge', {
          params: { path: { alertId } },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
  })
}

/**
 * 什么时候才吵你。设计稿左栏第一张卡。
 *
 * `title` 与 `description` 都是**后端渲染好的句子**，前端不拼。
 * 四个投递档位的人话走 `alert.channel.*`。
 */
export function useNotificationRules() {
  return useQuery({
    queryKey: ['notification-rules'] as const,
    queryFn: async () => call('/notification-rules', () => api.GET('/notification-rules')),
  })
}

/**
 * 最近一次真出事。设计稿左栏第二张卡。
 *
 * **只用最近的一条**——设计稿这块的标题就是「最近一次」，它不是事故列表。
 */
export function useIncidents() {
  return useQuery({
    queryKey: ['incidents'] as const,
    queryFn: async () => call('/incidents', () => api.GET('/incidents')),
  })
}

// -------------------------------------------------------- 他们干过什么

/**
 * 时间线顶上那三个 chip。
 *
 * **值就是查询参数本身**，不是前端另起的一套名字：`all` 两个参数都不传，
 * 另外两档各自把对应的那个传成 `true`。流 A3 的第 5 条渲染规则说得很直白——
 * 后端 `Filter.Matches` 已经有答案了，前端**不做第二次过滤**。
 *
 * 光讲纪律还不够，这里有个具体会出错的地方：接口是分页的。
 * 在客户端过滤，用户看到的「全部出过问题的」其实只是「这一页里出过问题的」，
 * 而且页数越多错得越离谱。所以过滤只能在服务端做。
 */
export type TimelineFilter = 'all' | 'changed' | 'troubled'
export interface TimelineQuery {
  filter: TimelineFilter
  projectId?: string
  hostId?: string
  limit?: number
}

/**
 * 时间线。**走 `/timeline` 而不是 `/actions`。**
 *
 * ADR-0004 之后时间线有两个源：AI 在机器上干的事（`actions` 表）与
 * 人在控制面做的事（`audit_log`）。**合并、分页、筛选全在服务端做。**
 *
 * 为什么不能在这儿打两个接口再交错——两条都是会悄悄给出错结果的：
 *
 *   分页    两个源各自分页，交错出来的不是时间序的前 N 条。
 *           actions 密集而 audit 稀疏时，第 2 页会冒出比第 1 页末尾更新的条目；
 *           反过来则翻十页都看不到一条审计记录，而「上周谁把腾讯云的密码换了」
 *           正是要靠它回答的
 *   筛选    两个源的判据根本不是同一个：Action 那边是「有没有能撤的退路」，
 *           审计那边是「这个 verb 改了状态还是只读了一眼」。
 *           前端没法替后端定义后者
 *
 * `cursor` 是不透明串，里面编码了两个源各自的位置。**原样传回去，不要自己拆。**
 */
export function useTimeline(input: TimelineFilter | TimelineQuery = 'all') {
  const opts = normalizeTimelineQuery(input)

  return useQuery({
    queryKey: queryKeys.timeline(opts),
    queryFn: async () =>
      call('/timeline', () =>
        api.GET('/timeline', {
          params: {
            query: {
              project_id: opts.projectId,
              host_id: opts.hostId,
              limit: opts.limit,
              changed_only: opts.filter === 'changed' ? true : undefined,
              troubled_only: opts.filter === 'troubled' ? true : undefined,
            },
          },
        }),
      ),
  })
}

export function useProjectTimeline(projectId: string) {
  return useTimeline({ filter: 'all', projectId, limit: 4 })
}

function normalizeTimelineQuery(input: TimelineFilter | TimelineQuery): TimelineQuery {
  if (typeof input === 'string') return { filter: input }
  return input
}

/**
 * 批一条红级动作：「这一步碰到钱了，得你点一下」那一下。
 *
 * ------------------------------------------------------------------
 * # 这个 hook 存在之前，红级动作在界面上批不了
 *
 * 服务端那条 `POST /actions/{actionId}/approve` 2026-08 就挂着了
 * （`approveGuard`，带 `IntentApproveRedAction` 的二次验证），
 * 而控制台**一处都没调过**：`today` 屏「等你确认的」那张卡只把标题列出来，
 * `timeline` 上 `awaiting_approval` 只是一个琥珀色的点。
 * 也就是说一条红级动作一旦停在那儿，**用户没有任何办法让它继续**——
 * 不是被拦住，是没有路。
 *
 * # 请求体是空的，而且必须是空的
 *
 * 谁点的、什么时候、从哪个 IP、过没过二次验证，四样全由服务端观察得到。
 * 让调用方传等于让它自己声明「我是谁、我验过了」，而 CLAUDE.md §10
 * 要记的是**观察到的**，不是**被告知的**。
 *
 * # 令牌绑到这条 actionId 上
 *
 * intent 必须是 `approve_red_action`，resource 必须是这条 actionId。
 * 红级动作之间的差别可能是「重启一个服务」与「改结算逻辑」，
 * 一张令牌通吃是不能接受的——服务端 2026-09-01 起逐字比对，
 * 漏传会当场 403 而不是悄悄变成通吃。
 * ------------------------------------------------------------------
 */
export function useApproveAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { actionId: string; stepUpToken: string }) =>
      call('/actions/{actionId}/approve', () =>
        api.POST('/actions/{actionId}/approve', {
          params: {
            path: { actionId: input.actionId },
            header: { 'X-Npc-Step-Up': input.stepUpToken },
          },
        }),
      ),
    onSettled: () => {
      // 批完这条动作就开始跑了：时间线多一条、今天屏「等你确认的」少一条、
      // 总览上的计数跟着变。三处都要重取，漏一处会让他以为没点成。
      void queryClient.invalidateQueries({ queryKey: ['timeline'] })
      void queryClient.invalidateQueries({ queryKey: queryKeys.today })
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
    },
  })
}

/**
 * 撤销这次改动。
 *
 * **返回的是新建的那一条 Action**（撤销本身也进时间线，M2-13 的验收标准），
 * 不是被撤的那一条。所以成功之后整张列表都要重取：被撤的那条 `undone_by`
 * 变了、`undoable` 变了，还多出来一条新的。
 *
 * 契约特意没有给这个接口加 `X-Npc-Step-Up`：产品原则第 3 条要求撤销这条路
 * **比动手更好走**，在它前面加一道生物识别是把那条原则反过来用。
 */
export function useUndoAction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (actionId: string) =>
      call('/actions/{actionId}/undo', () =>
        api.POST('/actions/{actionId}/undo', { params: { path: { actionId } } }),
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['timeline'] })
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard })
    },
  })
}

// ------------------------------------------------------------ 加一台机器

/** 解析贴进来的那一坨。M1 阶段是纯规则版，零模型调用。 */
export function useParseSshInput() {
  return useMutation({
    mutationFn: async (raw: string) =>
      call('/onboarding/parse', () => api.POST('/onboarding/parse', { body: { raw } })),
  })
}

/**
 * 开始接入。
 *
 * 原始密码或私钥只在这一次请求里出现，用完当场从内存销毁——所以这里**不进任何缓存**，
 * 也不写 localStorage，参数直接从输入框传到请求体。
 */
export function useStartOnboarding() {
  return useMutation({
    mutationFn: async (input: {
      address: string
      port?: number
      username?: string
      secret: Schemas['SshSecret']
    }) => call('/onboarding', () => api.POST('/onboarding', { body: input })),
  })
}

/**
 * 接到哪一步了。
 *
 * 人话进度走 SSE（`useProbeStream`），但「猜出来是什么」这份结构化结果在档案里，
 * 所以这里轮一下状态。没接完之前每 1.5 秒问一次，接完就停——
 * 「沉默是好消息」也适用于 HTTP 请求，别让浏览器一直嗡嗡响。
 */
export function useOnboarding(onboardingId: string | null) {
  return useQuery({
    queryKey: ['onboarding', onboardingId] as const,
    enabled: Boolean(onboardingId),
    queryFn: async () =>
      call('/onboarding/{onboardingId}', () =>
        api.GET('/onboarding/{onboardingId}', {
          params: { path: { onboardingId: onboardingId! } },
        }),
      ),
    // 判据与 SSE 那条流共用**同一份**（`noMoreEvents`）。
    // 原来这儿一份、屏里一份，分家的结果是轮询停了而流没停
    refetchInterval: (query) => (noMoreEvents(query.state.data) ? false : 1_500),
  })
}

/** 确认「我猜这台是」。什么都不改就直接收下猜的那份。 */
export function useConfirmOnboarding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      onboardingId: string
      name?: string
      summary?: string
      projectId?: string
      labelIds?: Schemas['HostLabelId'][]
    }) => {
      const body: {
        name?: string
        summary?: string
        project_id?: string
        label_ids?: Schemas['HostLabelId'][]
      } = {}
      if (input.name) body.name = input.name
      if (input.summary) body.summary = input.summary
      if (input.projectId) body.project_id = input.projectId
      if (input.labelIds) body.label_ids = input.labelIds

      return call('/onboarding/{onboardingId}/confirm', () =>
        api.POST('/onboarding/{onboardingId}/confirm', {
          params: { path: { onboardingId: input.onboardingId } },
          body,
        }),
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hosts })
    },
  })
}

/** 排班表。读 GET /api/v1/duty/skills，不发明公开技能契约。 */
export function useDebugSkills() {
  return useQuery({
    queryKey: queryKeys.debugSkills,
    queryFn: getDebugSkills,
  })
}

// ---------------------------------------------------------------- 模块演进

/**
 * AI OS 自进化那一组的 hook。**八读三写**，与冻结合同 §5 一一对应。
 *
 * 这里没有、而且不会有：接受、执行、派活、回滚。要动手走 Action / Policy /
 * Capability / Audit 那条路——`ai-os.test.ts` 里有一条用例盯着这件事。
 */
export function useAiModules(projectId: string) {
  return useQuery({
    queryKey: queryKeys.aiModules(projectId),
    queryFn: () => listAiModules(projectId),
  })
}

export function useAiModule(projectId: string, moduleId: string | null) {
  return useQuery({
    queryKey: queryKeys.aiModule(projectId, moduleId ?? ''),
    queryFn: () => getAiModule(projectId, moduleId as string),
    enabled: Boolean(moduleId),
  })
}

export function useModuleEvaluationRuns(projectId: string, filter: AiOsModuleFilter = {}) {
  return useQuery({
    queryKey: queryKeys.aiEvaluations(projectId, filter.moduleId),
    queryFn: () => listModuleEvaluationRuns(projectId, filter),
  })
}

export function useModuleEvaluationRun(projectId: string, evaluationId: string | null) {
  return useQuery({
    queryKey: queryKeys.aiEvaluation(projectId, evaluationId ?? ''),
    queryFn: () => getModuleEvaluationRun(projectId, evaluationId as string),
    enabled: Boolean(evaluationId),
  })
}

export function useImprovementProposals(projectId: string, filter: AiOsModuleFilter = {}) {
  return useQuery({
    queryKey: queryKeys.aiProposals(projectId, filter.moduleId),
    queryFn: () => listImprovementProposals(projectId, filter),
  })
}

export function useImprovementProposal(projectId: string, proposalId: string | null) {
  return useQuery({
    queryKey: queryKeys.aiProposal(projectId, proposalId ?? ''),
    queryFn: () => getImprovementProposal(projectId, proposalId as string),
    enabled: Boolean(proposalId),
  })
}

export function useVerifiedLearningEntries(projectId: string, filter: AiOsModuleFilter = {}) {
  return useQuery({
    queryKey: queryKeys.aiLearning(projectId, filter.moduleId),
    queryFn: () => listVerifiedLearningEntries(projectId, filter),
  })
}

export function useVerifiedLearningEntry(projectId: string, entryId: string | null) {
  return useQuery({
    queryKey: queryKeys.aiLearningEntry(projectId, entryId ?? ''),
    queryFn: () => getVerifiedLearningEntry(projectId, entryId as string),
    enabled: Boolean(entryId),
  })
}

/**
 * 提一条改进提案。**这一组唯一的写。**
 *
 * # 幂等键由调用方传进来，这一层不生成
 *
 * 别处几个 mutation 是在 `mutationFn` 里 `newIdempotencyKey()` 的，
 * **这里刻意不那样**：那样一来每次重试都是一个新键，幂等就成了摆设，
 * 而它失效的样子和正常一模一样。
 *
 * 契约那段话把代价说得很直白：一条重复的提案会真的派出去一次开发任务。
 * 所以键在**表单打开的那一刻**生成一次，「再试一次」原样带回来，
 * 提交成功之后才换新的。
 *
 * # 成功之后重取列表，不往缓存里塞
 *
 * 服务端回的那条提案带着它自己判定的 `state`（一律 `proposed`）。
 * 把它手工塞进列表缓存的话，塞进去的是**我以为服务端会给的形状**——
 * 而列表的排序、分页游标都是服务端的判断。重取一次，慢一点，但看到的是真的。
 *
 * 重取用的是前四段前缀，**故意不带 moduleId**：新提案属于某一个模块，
 * 而「全部」那份缓存也一样过期了。只重取当前那份筛选的话，
 * 切回「全部」看到的是一份少一条的旧列表。
 */
export function useCreateImprovementProposal(projectId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { input: ImprovementProposalCreate; idempotencyKey: string }) =>
      createImprovementProposal(projectId, vars.input, vars.idempotencyKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ai-os', 'proposals'],
      })
    },
  })
}

/**
 * 让一条停下来的提案再走一遍。
 *
 * # 键与 `expected_version` 都由调用方传进来
 *
 * 键的寿命归 `app/ai-os-operation.ts` 管（失败过的键是终态，换新的；
 * 没拿到响应的沿用同一把）。在这一层现生成的话，那套判断就没地方落脚了。
 *
 * `expected_version` 同理：它是**调用方读到的那个版本**，
 * 这一层从缓存里现取的话，取到的可能是刚被别人改过的那一份，
 * 于是 CAS 挡的东西就被绕过去了——而挡不住的样子和挡住了一模一样。
 *
 * # 成功之后重取，而且**重放时也重取**
 *
 * 重放没有 bump 版本，看起来「什么都没变，不用重取」。
 * 但我们本来就不知道这中间别人改没改——重放告诉我们的只是
 * 「这一次没动」，不是「这段时间没人动过」。
 */
export function useRetryImprovementProposal(projectId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      proposalId: string
      input: ProposalRetryRequest
      idempotencyKey: string
    }) => retryImprovementProposal(projectId, vars.proposalId, vars.input, vars.idempotencyKey),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'ai-os', 'proposals'],
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.aiProposal(projectId, vars.proposalId),
      })
    },
  })
}

/**
 * 别再用这条经验。
 *
 * 与重试走同一张台账（`op = disable`），所以键的寿命规矩完全一样。
 */
export function useDisableVerifiedLearningEntry(projectId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      entryId: string
      input: LearningEntryDisableRequest
      idempotencyKey: string
    }) => disableVerifiedLearningEntry(projectId, vars.entryId, vars.input, vars.idempotencyKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ai-os', 'learning'] })
    },
  })
}


export type { ActivityQuery, ActivityView } from './activity-query'

async function fetchActivityPage(opts: ActivityQuery, before?: string) {
  const time = activityTimeBounds(opts.time)
  return call('/activity', () =>
    api.GET('/activity', {
      params: {
        query: {
          view: opts.view,
          project_id: opts.projectId,
          scope: opts.scope,
          type: opts.type,
          status: opts.status,
          actor: opts.actor,
          q: opts.q,
          occurred_after: time.occurredAfter,
          occurred_before: time.occurredBefore,
          before: before ?? opts.before,
          limit: opts.limit,
        },
      },
    }),
  )
}

export function useActivityList(opts: ActivityQuery = {}) {
  return useQuery({
    queryKey: queryKeys.activity(opts),
    queryFn: () => fetchActivityPage(opts),
    placeholderData: keepPreviousData,
  })
}

export function useInfiniteActivityList(opts: ActivityQuery = {}) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.activity({ ...opts, before: undefined }), 'pages'] as const,
    queryFn: ({ pageParam }) => fetchActivityPage(opts, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.has_more ? page.next_cursor ?? undefined : undefined,
    // The client can safely narrow all/important/action while the next page arrives.
    // Following membership is server-owned, so reusing another view would show false rows.
    placeholderData: opts.view === 'following' ? undefined : keepPreviousData,
  })
}

export function useActivityItem(activityId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.activityItem(activityId ?? ''),
    enabled: Boolean(activityId),
    queryFn: async () =>
      call('/activity/{activityId}', () =>
        api.GET('/activity/{activityId}', {
          params: { path: { activityId: activityId! } },
        }),
      ),
  })
}

export function useMarkActivityRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (streamSeq: number) =>
      call('/activity/read', () =>
        api.POST('/activity/read', { body: { stream_seq: streamSeq } }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['activity'] })
    },
  })
}

export function useFollowActivity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { object_type: 'project' | 'task' | 'site' | 'resource' | 'agent'; object_id: string; follow?: boolean }) =>
      call('/activity/follow', () => api.POST('/activity/follow', { body: input })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['activity'] })
    },
  })
}
